import * as d3 from "d3";
export { AnalysisGraph };

class AnalysisGraph {
  async horizontalTimelineGraph(id, data) {
    const height = 100;
    const totalDuration = d3.max(data, (d) => d.end);

    function getResponsiveWidth(innerWidth) {
      const baseWindowWidth = 1900; // base screen width
      const baseContentWidth = 1200; // content width at 1900px
      const step = 40; // every 40px change
      const stepChange = 25; // change width by 30px per step
      const min = 600; // lower limit for content width
      const max = 3000; // upper limit for content width

      const steps = Math.floor((innerWidth - baseWindowWidth) / step);
      const dynamicWidth = baseContentWidth + steps * stepChange;

      return Math.max(min, Math.min(dynamicWidth, max));
    }
    const width = getResponsiveWidth(window.innerWidth);

    //create svg
    const svg = d3
      .select(id)
      .append("svg")
      //   .attr("width", width)
      //   .attr("height", height)
      .attr("viewBox", `0 0 ${width} ${height}`)
      .style("font-family", "sans-serif");

    //define the x scale
    const x = d3
      .scaleLinear()
      .domain([0, totalDuration])
      .range([0, width - 40]);

    //create a grop for the bars
    const barGroup = svg.append("g").attr("transform", "translate(20, 30)");

    //draw the bars
    const segments = barGroup
      .selectAll(".segment")
      .data(data)
      .enter()
      .append("g")
      .attr("transform", (d) => `translate(${x(d.start)}, 0)`);

    const rectHeight = 35;
    const radius = 10;

    const tooltip = d3
      .select("body")
      .append("div")
      .attr("class", "timeline-tooltip")
      .style("position", "absolute")
      .style("background", "#fff")
      .style("padding", "6px 12px")
      .style("border", "1px solid #ccc")
      .style("border-radius", "4px")
      .style("pointer-events", "none")
      .style("font-family", "lato")
      .style("font-size", "12px")
      .style("color", "#333")
      .style("box-shadow", "0px 2px 5px rgba(0,0,0,0.1)")
      .style("opacity", 0)
      .style("z-index", 1000);

    tooltip.append("div").attr("class", "tooltip-content");

    tooltip
      .append("div")
      .attr("class", "tooltip-arrow")
      .style("position", "absolute")
      .style("top", "50%")
      .style("left", "-6px")
      .style("transform", "translateY(-50%)")
      .style("width", "0")
      .style("height", "0")
      .style("border-top", "6px solid transparent")
      .style("border-bottom", "6px solid transparent")
      .style("border-right", "6px solid #fff")
      .style("filter", "drop-shadow(-1px 0 1px rgba(0,0,0,0.1))");

    function getInMinute(seconds) {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      return `${minutes} min ${remainingSeconds.toFixed(0)} sec`;
    }

    // Create an "Inverted Mask" / Window Frame
    // This is a white shape that covers everything *outside* the rounded bar.
    // Use fill-rule="evenodd" with an outer rectangle and an inner rounded rectangle.

    const w = width;
    const h = rectHeight; // Use rectHeight for the bar height
    const r = radius;
    const outerMargin = 10; // Extra coverage around the outside

    const maskPath = `
      M ${-outerMargin},${-outerMargin} 
      H ${w + outerMargin} 
      V ${h + outerMargin} 
      H ${-outerMargin} 
      Z
      
      M ${0},${r} 
      A ${r},${r} 0 0 1 ${r},${0} 
      H ${w - r} 
      A ${r},${r} 0 0 1 ${w},${r} 
      V ${h - r} 
      A ${r},${r} 0 0 1 ${w - r},${h} 
      H ${r} 
      A ${r},${r} 0 0 1 ${0},${h - r} 
      Z
    `;

    barGroup
      .append("path")
      .attr("d", maskPath)
      .attr("fill", "#ffffff")
      .attr("fill-rule", "evenodd")
      .style("pointer-events", "none");

    //draw the bars with rounded corners and colors
    segments
      .append("path")
      .attr("d", (d, i) => {
        let w = x(d.end) - x(d.start);
        const h = rectHeight;
        let r = radius;
        const minRoundedWidth = 1.1 * radius;

        if (i === 0) {
          if (w < minRoundedWidth) {
            w = minRoundedWidth;
            r = Math.min(radius, w / 2);
          }

          return `
          M${r},0 
          H${w}
          V${h}
          H${r}
          A${r},${r} 0 0 1 0,${h - r}
          V${r}
          A${r},${r} 0 0 1 ${r},0
          Z
          `;
        }

        if (i === data.length - 1) {
          if (w < minRoundedWidth) {
            w = minRoundedWidth;
            r = Math.min(radius, w / 2);
          }

          return `
          M0,0
          H${w - r}
          A${r},${r} 0 0 1 ${w},${r}
          V${h - r}
          A${r},${r} 0 0 1 ${w - r},${h}
          H0
          Z
          `;
        }

        return `
        M0,0
        H${w}
        V${h}
        H0
        Z
        `;
      })
      .attr("fill", (d) => d.color)
      .on("mouseover", function (event, d) {
        tooltip.style("opacity", 1);

        tooltip.select(".tooltip-content").html(`
        <strong>${d.speaker}</strong><br/>
        Start: ${getInMinute(d.start)}<br/>
        End: ${getInMinute(d.end)}
      `);
      })
      .on("mousemove", function (event) {
        const tooltipNode = tooltip.node();
        tooltip
          .style("left", `${event.pageX}px`)
          .style("top", `${event.pageY - tooltipNode.offsetHeight / 2}px`);
      })
      .on("mouseout", function () {
        tooltip.style("opacity", 0);
      });
  }

  //   async communicationRatingGraphs(id, rawDatas) {
  //     console.log("rawDatas", rawDatas);
  //     //to check with static data uncomment below
  //     const rawData = {
  //       Alice: { role: "Panelist", Good: 40, Neutral: 20, Rude: 40 },
  //       Bob: { role: "Candidate", Good: 30, Neutral: 10, Rude: 60 },
  //       Clara: { role: "Manager", Good: 5, Neutral: 5, Rude: 90 },
  //       Shravan: { role: "Manager", Good: 40, Neutral: 20, Rude: 40 },
  //       Sharath: { role: "Manager", Good: 40, Neutral: 20, Rude: 40 },
  //       David: { role: "Panelist", Good: 35, Neutral: 25, Rude: 40 },
  //       Emma: { role: "Candidate", Good: 50, Neutral: 30, Rude: 20 },
  //     };

  //     const data = Object.entries(rawData).flatMap(([name, obj]) => {
  //       const { role, ...categories } = obj;
  //       return Object.entries(categories).map(([category, value]) => ({
  //         name,
  //         role,
  //         category,
  //         value,
  //       }));
  //     });

  //     // Define layout sizes and margins
  //     let margin = { top: 10, right: 100, bottom: 50, left: 100 };

  //     function getResponsiveWidth(innerWidth) {
  //       const baseWidth = 1900;
  //       const baseOutputWidth = 950;
  //       const step = 40; // Pixels per step
  //       const stepChange = 25; // Width change per step
  //       const min = 450; // Minimum allowed width
  //       const max = 3000; // Maximum allowed width

  //       const steps = Math.floor((innerWidth - baseWidth) / step);
  //       const width = baseOutputWidth + steps * stepChange;
  //       return Math.max(min, Math.min(width, max));
  //     }
  //     const width = getResponsiveWidth(window.innerWidth);

  //     const height = 330 - margin.bottom;

  //     // Get unique names and categories
  //     const names = Array.from(new Set(data.map((d) => d.name)));
  //     const categories = Array.from(new Set(data.map((d) => d.category)));

  //     // Calculate if scrolling is needed
  //     const maxVisibleItems = 3;
  //     const needsScrolling = names.length > maxVisibleItems;
  //     const itemHeight = height / Math.min(names.length, maxVisibleItems);
  //     const totalContentHeight = needsScrolling
  //       ? names.length * (itemHeight + 10)
  //       : height;

  //     // Create main SVG container
  //     const container = d3.select(id);
  //     // Clear any existing content
  //     container.selectAll("*").remove();

  //     // Create wrapper div/wrapper group for scrolling
  //     const wrapper = container
  //       .append("div")
  //       .style("position", "relative")
  //       .style("width", "100%")
  //       .style("height", `${height + margin.top + margin.bottom}px`)
  //       .style("overflow", "hidden");

  //     // Create scrollable content area
  //     const scrollableArea = wrapper
  //       .append("div")
  //       .style("position", "absolute")
  //       .style("top", "0")
  //       .style("left", "0")
  //       .style("width", "100%")
  //       .style("height", needsScrolling ? `${height}px` : "100%")
  //       .style("overflow-y", needsScrolling ? "auto" : "hidden")
  //       .style("overflow-x", "hidden");

  //     // Create SVG for the chart content
  //     const svg = scrollableArea
  //       .append("svg")
  //       .attr("width", width + margin.left + margin.right)
  //       .attr(
  //         "height",
  //         totalContentHeight + margin.top + (needsScrolling ? 0 : margin.bottom)
  //       )

  //     // Append main chart group inside the SVG
  //     const g = svg
  //       .append("g")
  //       .attr(
  //         "viewBox",
  //         `0 0 ${width + margin.left + margin.right} ${
  //           totalContentHeight + margin.top + (needsScrolling ? 0 : margin.bottom)
  //         }`
  //       )
  //       .attr("preserveAspectRatio", "xMidYMid meet")
  //       .attr("transform", `translate(${margin.left},${margin.top})`);

  //     // Define scales

  //     //outerband scales to show the names
  //     const y0 = d3
  //       .scaleBand()
  //       .domain(names)
  //       .range([0, needsScrolling ? totalContentHeight : height])
  //       .padding(0.3);

  //     //innerband scales to show the categories
  //     const y1 = d3
  //       .scaleBand()
  //       .domain(categories)
  //       .range([0, y0.bandwidth()])
  //       .padding(0.01);

  //     // Define scales for x-axis
  //     const x = d3.scaleLinear().domain([0, 100]).range([0, width]);

  //     //color scale based on categories
  //     const color = d3
  //       .scaleOrdinal()
  //       .domain(categories)
  //       .range(["#269C8A", "#F5D06E", "#AD1927"]);

  //     // Grid lines for scrollable content
  //     const contentHeight = needsScrolling ? totalContentHeight : height;

  //     const xAxisGrid = d3
  //       .axisBottom(x)
  //       .tickSize(-contentHeight)
  //       .tickFormat("")
  //       .ticks(10);

  //     g.append("g")
  //       .attr("class", "grid")
  //       .attr("transform", `translate(0, ${contentHeight})`)
  //       .call(xAxisGrid)
  //       .selectAll("line")
  //       .attr("stroke", "#F0F0F0")
  //       .attr("opacity", 0.7);

  //     // Y-axis grid lines
  //     g.append("g")
  //       .attr("class", "grid")
  //       .call(d3.axisLeft(y0).tickSize(-width).tickFormat(""))
  //       .selectAll("line")
  //       .attr("stroke", "#F0F0F0")
  //       .attr("opacity", 0.7);

  //     // Add top horizontal grid line
  //     g.append("line")
  //       .attr("x1", 0)
  //       .attr("x2", width)
  //       .attr("y1", 0)
  //       .attr("y2", 0)
  //       .attr("stroke", "#F0F0F0")
  //       .attr("opacity", 0.7);

  //     // Add bottom horizontal grid line
  //     g.append("line")
  //       .attr("x1", 0)
  //       .attr("x2", width)
  //       .attr("y1", contentHeight)
  //       .attr("y2", contentHeight)
  //       .attr("stroke", "#F0F0F0")
  //       .attr("opacity", 0.7);

  //     // Y-Axis with names of roles
  //     g.append("g")
  //       .call(d3.axisLeft(y0).tickSize(0))
  //       .selectAll("text")
  //       .attr("x", -30)
  //       .attr("dy", "0.35em")
  //       .style("font-family", "Montserrat")
  //       .style("font-weight", "600")
  //       .style("fill", "#333");

  //     // Bars
  //     const grouped = d3.groups(data, (d) => d.name);
  //     const groups = g
  //       .selectAll(".role-group")
  //       .data(grouped)
  //       .enter()
  //       .append("g")
  //       .attr("class", "role-group")
  //       .attr("transform", (d) => `translate(0, ${y0(d[0])})`);

  //     groups.each(function ([name, values]) {
  //       console.log(name);
  //       const group = d3.select(this);
  //       const roleInitial = values[0].role[0].toUpperCase();

  //       // to show role initials on the left side with circles
  //       group
  //         .append("circle")
  //         .attr("cx", -15)
  //         .attr("cy", y0.bandwidth() / 2)
  //         .attr("r", 8)
  //         .attr("fill", roleInitial == "C" ? "#20639b" : "#EF9727");

  //       group
  //         .append("text")
  //         .attr("x", -15)
  //         .attr("y", y0.bandwidth() / 2 + 4)
  //         .attr("text-anchor", "middle")
  //         .attr("font-size", "11px")
  //         .attr("font-family", "Montserrat")
  //         .attr("font-weight", "bold")
  //         .text(roleInitial);

  //       // to create bars for each category using values
  //       group
  //         .selectAll("rect")
  //         .data(values)
  //         .enter()
  //         .append("rect")
  //         .attr("x", 0)
  //         .attr("y", (d) => y1(d.category))
  //         .attr("width", (d) => x(d.value))
  //         .attr("height", y1.bandwidth())
  //         .attr("fill", (d) => color(d.category))
  //         .attr("rx", 1)
  //         .attr("ry", 1);

  //       // to show values on top of the bars
  //       group
  //         .selectAll("text.value")
  //         .data(values)
  //         .enter()
  //         .append("text")
  //         .attr("class", "value")
  //         .attr("x", (d) => x(d.value) + 5)
  //         .attr("y", (d) => y1(d.category) + y1.bandwidth() / 2 + 4)
  //         .attr("font-family", "Inter")
  //         .attr("font-size", "9px")
  //         .attr("fill", (d) => color(d.category))
  //         .text((d) => `${d.value > 0 ? d.value : ""}`);
  //     });

  //     // Create fixed X-axis at the bottom (outside scrollable area)
  //     if (needsScrolling) {
  //       const fixedAxisSvg = wrapper
  //         .append("svg")
  //         .style("position", "absolute")
  //         .style("bottom", "0")
  //         .style("left", "0")
  //         .style("width", `${width + margin.left + margin.right}px`)
  //         .style("height", `${margin.bottom}px`)
  //         .style("background", "white");
  //       // .style("border-top", "1px solid #F0F0F0");

  //       const fixedAxisG = fixedAxisSvg
  //         .append("g")
  //         .attr("transform", `translate(${margin.left},0)`);

  //       fixedAxisG
  //         .append("line")
  //         .attr("x1", 0)
  //         .attr("x2", width)
  //         .attr("y1", 0)
  //         .attr("y2", 0)
  //         .attr("stroke", "#F0F0F0")
  //         .attr("stroke-width", 1);

  //       // Bottom X-Axis (fixed)
  //       fixedAxisG
  //         .append("g")
  //         .attr("class", "x-axis")
  //         .call(
  //           d3
  //             .axisBottom(x)
  //             .ticks(10)
  //             .tickSize(0)
  //             .tickFormat((d) => `${d}`)
  //         )
  //         .selectAll("text")
  //         .style("font-family", "Montserrat")
  //         .style("font-size", "10px")
  //         .style("fill", "#72738E");

  //       // X-axis label (fixed)
  //       fixedAxisSvg
  //         .append("text")
  //         .attr("x", margin.left + width / 2)
  //         .attr("y", margin.bottom - 5)
  //         .attr("text-anchor", "middle")
  //         .style("font-family", "Montserrat")
  //         .style("font-size", "10px")
  //         .style("font-weight", "500")
  //         .style("fill", "#888888CC")
  //         .text("Tone Percentage(%)");

  //       fixedAxisSvg.selectAll(".axis path, .axis line").style("stroke", "none");
  //       fixedAxisSvg.selectAll("path.domain").style("stroke", "none");
  //     } else {
  //       // Bottom X-Axis for non-scrolling case
  //       g.append("g")
  //         .attr("class", "x-axis")
  //         .attr("transform", `translate(0, ${height})`)
  //         .call(
  //           d3
  //             .axisBottom(x)
  //             .ticks(10)
  //             .tickSize(0)
  //             .tickFormat((d) => `${d}`)
  //         )
  //         .selectAll("text")
  //         .style("font-family", "Montserrat")
  //         .style("font-size", "10px")
  //         .style("fill", "#72738E");

  //       // X-axis label for non-scrolling case
  //       svg
  //         .append("text")
  //         .attr("x", margin.left + width / 2)
  //         .attr("y", height + margin.top + margin.bottom - 5)
  //         .attr("text-anchor", "middle")
  //         .style("font-family", "Montserrat")
  //         .style("font-size", "10px")
  //         .style("fill", "#888888CC")
  //         .text("Tone Percentage(%)");
  //     }

  //     svg.selectAll(".axis path, .axis line").style("stroke", "none");
  //     svg.selectAll("path.domain").style("stroke", "none");
  //   }

  async communicationRatingGraph(id, rawData) {
    // console.log("rawDatas", rawDatas);
    //to check with static data uncomment below
    // const rawData = {
    //   Alice: { role: "Panelist", Good: 40, Neutral: 20, Rude: 40 },
    //   Bob: { role: "Candidate", Good: 30, Neutral: 10, Rude: 60 },
    //   Clara: { role: "Manager", Good: 5, Neutral: 5, Rude: 90 },
    //   Shravan: { role: "Manager", Good: 40, Neutral: 20, Rude: 40 },
    //   Sharath: { role: "Manager", Good: 40, Neutral: 20, Rude: 40 },
    //   David: { role: "Panelist", Good: 35, Neutral: 25, Rude: 40 },
    //   Emma: { role: "Candidate", Good: 50, Neutral: 30, Rude: 20 },
    // };

    const data = Object.entries(rawData).flatMap(([name, obj]) => {
      const { role, ...categories } = obj;
      return Object.entries(categories).map(([category, value]) => ({
        name,
        role,
        category,
        value,
      }));
    });

    // Define layout sizes and margins
    let margin = { top: 10, right: 100, bottom: 50, left: 100 };

    function getResponsiveWidth(innerWidth) {
      const baseWidth = 1900;
      const baseOutputWidth = 950;
      const step = 40; // Pixels per step
      const stepChange = 25; // Width change per step
      const min = 450; // Minimum allowed width
      const max = 3000; // Maximum allowed width

      const steps = Math.floor((innerWidth - baseWidth) / step);
      const width = baseOutputWidth + steps * stepChange;
      return Math.max(min, Math.min(width, max));
    }
    const width = getResponsiveWidth(window.innerWidth);

    const height = 330 - margin.bottom;

    // Get unique names and categories
    const names = Array.from(new Set(data.map((d) => d.name)));
    const categories = Array.from(new Set(data.map((d) => d.category)));

    // Calculate if scrolling is needed
    const maxVisibleItems = 3;
    const needsScrolling = names.length > maxVisibleItems;
    const itemHeight = height / Math.min(names.length, maxVisibleItems);
    const totalContentHeight = needsScrolling
      ? names.length * (itemHeight + 10)
      : height;

    // Create main container
    const container = d3.select(id);
    // Clear any existing content
    container.selectAll("*").remove();

    // Create wrapper div for layout management
    const wrapper = container
      .append("div")
      .style("position", "relative")
      .style("width", "100%")
      .style("height", `${height + margin.top + margin.bottom}px`)
      .style("overflow", "hidden");

    if (needsScrolling) {
      // Create scrollable content area
      const scrollableArea = wrapper
        .append("div")
        .style("position", "absolute")
        .style("top", "0")
        .style("left", "0")
        .style("width", "100%")
        .style("height", `${height}px`)
        .style("overflow-y", "auto")
        .style("overflow-x", "hidden");

      // Create SVG for the chart content with viewBox
      const svg = scrollableArea
        .append("svg")
        .style("width", "100%")
        .style("height", "auto")
        .attr(
          "viewBox",
          `0 0 ${width + margin.left + margin.right} ${
            totalContentHeight + margin.top
          }`
        )
        .attr("preserveAspectRatio", "xMidYMid meet");

      // Append main chart group inside the SVG
      const g = svg
        .append("g")
        .attr("transform", `translate(${margin.left},${margin.top})`);

      // Define scales
      const y0 = d3
        .scaleBand()
        .domain(names)
        .range([0, totalContentHeight])
        .padding(0.3);

      const y1 = d3
        .scaleBand()
        .domain(categories)
        .range([0, y0.bandwidth()])
        .padding(0.01);

      const x = d3.scaleLinear().domain([0, 100]).range([0, width]);

      const color = d3
        .scaleOrdinal()
        .domain(categories)
        .range(["#269C8A", "#F5D06E", "#AD1927"]);

      // Grid lines
      const xAxisGrid = d3
        .axisBottom(x)
        .tickSize(-totalContentHeight)
        .tickFormat("")
        .ticks(10);

      g.append("g")
        .attr("class", "grid")
        .attr("transform", `translate(0, ${totalContentHeight})`)
        .call(xAxisGrid)
        .selectAll("line")
        .attr("stroke", "#F0F0F0")
        .attr("opacity", 0.7);

      // Y-axis grid lines
      g.append("g")
        .attr("class", "grid")
        .call(d3.axisLeft(y0).tickSize(-width).tickFormat(""))
        .selectAll("line")
        .attr("stroke", "#F0F0F0")
        .attr("opacity", 0.7);

      // Add top and bottom horizontal grid lines
      g.append("line")
        .attr("x1", 0)
        .attr("x2", width)
        .attr("y1", 0)
        .attr("y2", 0)
        .attr("stroke", "#F0F0F0")
        .attr("opacity", 0.7);

      g.append("line")
        .attr("x1", 0)
        .attr("x2", width)
        .attr("y1", totalContentHeight)
        .attr("y2", totalContentHeight)
        .attr("stroke", "#F0F0F0")
        .attr("opacity", 0.7);

      // Y-Axis with names
      g.append("g")
        .call(d3.axisLeft(y0).tickSize(0))
        .selectAll("text")
        .attr("x", -30)
        .attr("dy", "0.35em")
        .style("font-family", "Montserrat")
        .style("font-weight", "600")
        .style("fill", "#333");

      // Create bars
      const grouped = d3.groups(data, (d) => d.name);
      const groups = g
        .selectAll(".role-group")
        .data(grouped)
        .enter()
        .append("g")
        .attr("class", "role-group")
        .attr("transform", (d) => `translate(0, ${y0(d[0])})`);

      groups.each(function ([name, values]) {
        console.log(name);
        const group = d3.select(this);
        const roleInitial = values[0].role[0].toUpperCase();

        // Role initials with circles
        group
          .append("circle")
          .attr("cx", -15)
          .attr("cy", y0.bandwidth() / 2)
          .attr("r", 8)
          .attr("fill", roleInitial == "C" ? "#20639b" : "#EF9727");

        group
          .append("text")
          .attr("x", -15)
          .attr("y", y0.bandwidth() / 2 + 4)
          .attr("text-anchor", "middle")
          .attr("font-size", "11px")
          .attr("font-family", "Montserrat")
          .attr("font-weight", "bold")
          .text(roleInitial);

        // Create bars
        group
          .selectAll("rect")
          .data(values)
          .enter()
          .append("rect")
          .attr("x", 0)
          .attr("y", (d) => y1(d.category))
          .attr("width", (d) => x(d.value))
          .attr("height", y1.bandwidth())
          .attr("fill", (d) => color(d.category))
          .attr("rx", 1)
          .attr("ry", 1);

        // Value labels
        group
          .selectAll("text.value")
          .data(values)
          .enter()
          .append("text")
          .attr("class", "value")
          .attr("x", (d) => x(d.value) + 5)
          .attr("y", (d) => y1(d.category) + y1.bandwidth() / 2 + 4)
          .attr("font-family", "Inter")
          .attr("font-size", "9px")
          .attr("fill", (d) => color(d.category))
          .text((d) => `${d.value}`);
      });

      // Create fixed X-axis at the bottom (outside scrollable area)
      const fixedAxisSvg = wrapper
        .append("svg")
        .style("position", "absolute")
        .style("bottom", "0")
        .style("left", "0")
        .style("width", "100%")
        .style("height", `${margin.bottom}px`)
        .attr(
          "viewBox",
          `0 0 ${width + margin.left + margin.right} ${margin.bottom}`
        )
        .attr("preserveAspectRatio", "xMidYMid meet")
        .style("background", "white");

      const fixedAxisG = fixedAxisSvg
        .append("g")
        .attr("transform", `translate(${margin.left},0)`);

      fixedAxisG
        .append("line")
        .attr("x1", 0)
        .attr("x2", width)
        .attr("y1", 0)
        .attr("y2", 0)
        .attr("stroke", "#F0F0F0")
        .attr("stroke-width", 1);

      // Bottom X-Axis (fixed)
      fixedAxisG
        .append("g")
        .attr("class", "x-axis")
        .call(
          d3
            .axisBottom(x)
            .ticks(10)
            .tickSize(0)
            .tickFormat((d) => `${d}`)
        )
        .selectAll("text")
        .style("font-family", "Montserrat")
        .style("font-size", "10px")
        .style("fill", "#72738E");

      // X-axis label (fixed)
      fixedAxisSvg
        .append("text")
        .attr("x", margin.left + width / 2)
        .attr("y", margin.bottom - 5)
        .attr("text-anchor", "middle")
        .style("font-family", "Montserrat")
        .style("font-size", "10px")
        .style("font-weight", "500")
        .style("fill", "#888888CC")
        .text("Tone Percentage(%)");

      //   fixedAxisSvg.selectAll(".axis path, .axis line").style("stroke", "none");
      //   fixedAxisSvg.selectAll("path.domain").style("stroke", "none");
    } else {
      // For non-scrolling content, use single SVG approach
      const svg = wrapper
        .append("svg")
        .style("width", "100%")
        .style("height", "100%")
        .attr(
          "viewBox",
          `0 0 ${width + margin.left + margin.right} ${
            height + margin.top + margin.bottom
          }`
        )
        .attr("preserveAspectRatio", "xMidYMid meet");

      const g = svg
        .append("g")
        .attr("transform", `translate(${margin.left},${margin.top})`);

      // Define scales for non-scrolling
      const y0 = d3.scaleBand().domain(names).range([0, height]).padding(0.3);

      const y1 = d3
        .scaleBand()
        .domain(categories)
        .range([0, y0.bandwidth()])
        .padding(0.01);

      const x = d3.scaleLinear().domain([0, 100]).range([0, width]);

      const color = d3
        .scaleOrdinal()
        .domain(categories)
        .range(["#269C8A", "#F5D06E", "#AD1927"]);

      // Grid lines
      const xAxisGrid = d3
        .axisBottom(x)
        .tickSize(-height)
        .tickFormat("")
        .ticks(10);

      g.append("g")
        .attr("class", "grid")
        .attr("transform", `translate(0, ${height})`)
        .call(xAxisGrid)
        .selectAll("line")
        .attr("stroke", "#F0F0F0")
        .attr("opacity", 0.7);

      g.append("g")
        .attr("class", "grid")
        .call(d3.axisLeft(y0).tickSize(-width).tickFormat(""))
        .selectAll("line")
        .attr("stroke", "#F0F0F0")
        .attr("opacity", 0.7);

      g.append("line")
        .attr("x1", 0)
        .attr("x2", width)
        .attr("y1", 0)
        .attr("y2", 0)
        .attr("stroke", "#F0F0F0")
        .attr("opacity", 0.7);

      g.append("line")
        .attr("x1", 0)
        .attr("x2", width)
        .attr("y1", height)
        .attr("y2", height)
        .attr("stroke", "#F0F0F0")
        .attr("opacity", 0.7);

      // Y-Axis
      g.append("g")
        .call(d3.axisLeft(y0).tickSize(0))
        .selectAll("text")
        .attr("x", -30)
        .attr("dy", "0.35em")
        .style("font-family", "Montserrat")
        .style("font-weight", "600")
        .style("fill", "#333");

      // Create bars for non-scrolling
      const grouped = d3.groups(data, (d) => d.name);
      const groups = g
        .selectAll(".role-group")
        .data(grouped)
        .enter()
        .append("g")
        .attr("class", "role-group")
        .attr("transform", (d) => `translate(0, ${y0(d[0])})`);

      groups.each(function ([name, values]) {
        console.log(name);
        const group = d3.select(this);
        const roleInitial = values[0].role[0].toUpperCase();

        group
          .append("circle")
          .attr("cx", -15)
          .attr("cy", y0.bandwidth() / 2)
          .attr("r", 8)
          .attr("fill", roleInitial == "C" ? "#20639b" : "#EF9727");

        group
          .append("text")
          .attr("x", -15)
          .attr("y", y0.bandwidth() / 2 + 4)
          .attr("text-anchor", "middle")
          .attr("font-size", "11px")
          .attr("font-family", "Montserrat")
          .attr("font-weight", "bold")
          .text(roleInitial);

        group
          .selectAll("rect")
          .data(values)
          .enter()
          .append("rect")
          .attr("x", 0)
          .attr("y", (d) => y1(d.category))
          .attr("width", (d) => x(d.value))
          .attr("height", y1.bandwidth())
          .attr("fill", (d) => color(d.category))
          .attr("rx", 1)
          .attr("ry", 1);

        group
          .selectAll("text.value")
          .data(values)
          .enter()
          .append("text")
          .attr("class", "value")
          .attr("x", (d) => x(d.value) + 5)
          .attr("y", (d) => y1(d.category) + y1.bandwidth() / 2 + 4)
          .attr("font-family", "Inter")
          .attr("font-size", "9px")
          .attr("fill", (d) => color(d.category))
          .text((d) => `${d.value}`);
      });

      // Bottom X-Axis for non-scrolling case
      g.append("g")
        .attr("class", "x-axis")
        .attr("transform", `translate(0, ${height})`)
        .call(
          d3
            .axisBottom(x)
            .ticks(10)
            .tickSize(0)
            .tickFormat((d) => `${d}`)
        )
        .selectAll("text")
        .style("font-family", "Montserrat")
        .style("font-size", "10px")
        .style("fill", "#72738E");

      // X-axis label for non-scrolling case
      svg
        .append("text")
        .attr("x", margin.left + width / 2)
        .attr("y", height + margin.top + margin.bottom - 5)
        .attr("text-anchor", "middle")
        .style("font-family", "Montserrat")
        .style("font-size", "10px")
        .style("fill", "#888888CC")
        .text("Tone Percentage(%)");
    }
    wrapper.selectAll(".axis path, .axis line").style("stroke", "none");
    wrapper.selectAll("path.domain").style("stroke", "none");
  }
}
